import { Link } from "@tanstack/react-router";
import { Phone, MapPin, Clock, Instagram, MessageCircle, Mail, Info, ShieldCheck, Award, Link2 } from "lucide-react";
import { services, areas } from "@/lib/site-config";
import { useBusinessConfig } from "@/lib/use-business-config";
import { trackEvent } from "@/lib/tracking";
import { GoogleReviewsBadge } from "@/components/GoogleReviewsBadge";
import { QuickContact } from "@/components/QuickContact";
import { VerifiedAddressBadge } from "@/components/VerifiedAddressBadge";


export function Footer() {
  const biz = useBusinessConfig();

  return (
    <footer className="surface-dark grain-overlay">
      {/* Top credentials strip */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
          <Credential icon={ShieldCheck} label="MOT Testing Station" value="DVSA V106376" />
          <Credential icon={Award} label="Classes" value="1, 2, 3, 4, 5 & 7" />
          <Credential icon={Clock} label="Workshop Hours" value="Mon–Sat 9am–6pm" />
          <Credential icon={MapPin} label="Coverage" value="E14 & East London" />
        </div>
        <div className="mx-auto max-w-7xl px-4 md:px-6 pb-4">
          <VerifiedAddressBadge tone="dark" />
        </div>
      </div>


      {/* Direct contact hub strip — premium, action-first */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-14">
          <QuickContact
            location="footer_hub"
            variant="dark"
            eyebrow="Direct contact hub"
            headline="Call, WhatsApp or scan — we're easy to reach."
            subline="Whatever's quickest for you. Phone us in workshop hours, or scan the code to open all our links on your phone."
          />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-extrabold text-white tracking-tight">Woodseer Autos</span>
            </Link>
            <p className="mt-4 text-sm text-white/80 leading-relaxed max-w-sm">
              Honest mechanics. Clear advice. No nonsense. MOTs, servicing, diagnostics, mechanical repairs and bodywork — all under one roof in East London.
            </p>
            <div className="mt-6">
              <GoogleReviewsBadge compact tone="dark" />
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <a
                href={biz.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("instagram_click", { location: "footer" })}
                className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/30 px-3.5 py-2 text-xs font-bold text-white transition-colors"
                aria-label="Open Instagram @woodseerautos in a new tab"
              >
                <Instagram className="h-4 w-4" />
                @woodseerautos
              </a>
              <a
                href={biz.linktreeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("linktree_click", { location: "footer_brand" })}
                className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/30 px-3.5 py-2 text-xs font-bold text-white transition-colors"
                aria-label="Open Woodseer Autos Linktree"
              >
                <Link2 className="h-4 w-4" />
                Linktree
              </a>
              <a
                href={biz.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "footer" })}
                className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-[#25D366]/30 hover:border-[#25D366]/50 hover:bg-[#25D366]/8 transition-colors"
                aria-label="Message us on WhatsApp"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
              </a>
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "footer_brand" })}
                className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10 transition-colors"
                aria-label="Call Woodseer Autos"
              >
                <Phone className="h-4 w-4 text-white" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h3 className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em]">Services</h3>
            <ul className="mt-5 space-y-2.5">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}` as any} className="text-sm text-white/80 hover:text-white transition-colors">
                    {s.name}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link to="/pricing" className="text-sm text-white/80 hover:text-white transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/mot-checklist" className="text-sm text-white/80 hover:text-white transition-colors">
                  Free MOT Checklist
                </Link>
              </li>
              <li>
                <Link to="/free-car-resources" className="text-sm text-white/80 hover:text-white transition-colors">
                  Free Car Resources
                </Link>
              </li>
              <li>
                <Link to="/garage-near-me" className="text-sm text-white/80 hover:text-white transition-colors">
                  Garage Near Me
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-white/80 hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/book" className="text-sm text-primary hover:text-primary/80 font-bold transition-colors">
                  Request a Booking →
                </Link>
              </li>
            </ul>
          </div>

          {/* Areas */}
          <div className="lg:col-span-2">
            <h3 className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em]">Areas Covered</h3>
            <ul className="mt-5 space-y-2.5">
              {areas.map((a) => (
                <li key={a.slug}>
                  <Link to="/areas/$area" params={{ area: a.slug }} className="text-sm text-white/80 hover:text-white transition-colors">
                    {a.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h3 className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em]">Workshop & Contact</h3>
            <ul className="mt-5 space-y-4">
              <li>
                <a
                  href={biz.phoneHref}
                  onClick={() => trackEvent("call_click", { location: "footer" })}
                  className="inline-flex items-center gap-2.5 text-base text-white font-bold hover:text-primary transition-colors"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  {biz.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${biz.email}`}
                  onClick={() => trackEvent("email_click", { location: "footer" })}
                  className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  {biz.email}
                </a>
                <a
                  href="mailto:woodseerautos@gmail.com"
                  onClick={() => trackEvent("email_click", { location: "footer_admin" })}
                  className="mt-1 flex items-center gap-2.5 text-[11px] text-white/75 hover:text-white transition-colors"
                  title="Backup / admin contact — used for platform verification"
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  <span>woodseerautos@gmail.com</span>
                  <span className="text-white/65">· backup/admin</span>
                </a>
              </li>
              <li className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <address className="not-italic flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                  <div className="text-sm">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/85">Yard entrance · sat-nav</p>
                    <p className="mt-1 text-white font-bold">14 Copenhagen Place</p>
                    <p className="text-white/85">London E14 7DF</p>

                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/75">Registered office</p>
                      <p className="mt-1 text-[13px] text-white/85">{biz.address}, {biz.addressLine2}, {biz.postcode}</p>
                    </div>

                    <p
                      className="mt-3 inline-flex items-start gap-1.5 text-[11px] text-white/80 leading-relaxed cursor-help"
                      title="Our DVSA / Companies House registered address is on Pixley Street. The actual workshop yard you'll drive into is round the corner at 14 Copenhagen Place — always use the Copenhagen Place pin for sat-nav."
                    >
                      <Info className="h-3 w-3 shrink-0 mt-0.5 text-primary/70" />
                      <span>Two pins: Pixley St is legal/registered. Copenhagen Pl is where you actually drive in — sat-nav to that.</span>
                    </p>

                    <a
                      href={biz.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("directions_click", { location: "footer", kind: "entrance" })}
                      className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-primary hover:text-primary/80 transition-colors"
                    >
                      Get directions to the yard →
                    </a>
                  </div>
                </address>
              </li>
              <li className="flex items-start gap-2.5 text-sm">
                <Clock className="h-4 w-4 shrink-0 mt-0.5 text-white/80" />
                <dl className="space-y-1">
                  {biz.hours.map((h) => (
                    <div key={h.day} className="flex items-center gap-3">
                      <dt className="text-white/80 w-20">{h.day}</dt>
                      <dd className="text-white/80 font-semibold tabular-nums">{h.time}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/75">
          <p>© 2026 {biz.name}. All rights reserved. Independent garage — not affiliated with any manufacturer.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/official-business-information" className="hover:text-white/70 transition-colors">Official Business Info</Link>
            <Link to="/privacy" className="hover:text-white/70 transition-colors">Privacy</Link>
            <Link to="/cookies" className="hover:text-white/70 transition-colors">Cookies</Link>
            <Link to="/terms" className="hover:text-white/70 transition-colors">Terms</Link>
            <a href="/rss.xml" className="hover:text-white/70 transition-colors" aria-label="Blog RSS feed">RSS</a>
          </div>
        </div>
        <div className="mt-4 text-center text-[11px] text-white/65 leading-relaxed max-w-2xl mx-auto">
          <a
            href="https://www.rankmylocal.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-white/85 hover:text-primary transition-colors underline-offset-2 hover:underline"
          >
            Google Maps optimisation by RankMyLocal
          </a>{" "}
          — a London-based Google Business Profile &amp; local SEO agency for UK businesses.{" "}
          <a
            href="https://www.rankmylocal.co.uk/partner-network"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/80 hover:text-primary transition-colors underline-offset-2 hover:underline"
          >
            View partner network →
          </a>
          <span className="block mt-1 text-white/80">
            More about this site:{" "}
            <Link to="/managed-by" className="hover:text-white transition-colors underline-offset-2 hover:underline">
              how it's managed
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
}

function Credential({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary border border-primary/25 shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/75">{label}</p>
        <p className="mt-0.5 text-sm font-bold text-white truncate">{value}</p>
      </div>
    </div>
  );
}
